import {FC, useCallback, useEffect, useMemo, useState} from "react";
// components
import PageHeading from "../components/PageHeading";
import Button from "../components/Button";
// styles
import styles from "../styles/pages/estimation/EstimationPage.module.css";
import BasketInformation from "../components/BasketInformation";
import {IBasket} from "../@interfaces/basket.interface";
import {IQuote} from "../@interfaces/purchase.interface";
import Footer from "../components/Footer";
import {formatMoney, getCurrencySymbol, SUPPORTED_CURRENCIES} from "../utils/currency";
import Select from "../components/Select";
import {IWidgetConfiguration} from "../merchantConfig";
import {formatNoDecimals} from "../utils/number";
import { convert, MassUnit, MASS_UNITS } from "../utils/massUnit";

interface IEstimationPage {
  quote: IQuote | null;
  monetaryAmount: number;
  selectedCurrency: any;
  isLoading: boolean;
  basket: IBasket | null;
  onOffsetClick: () => void;
  merchantCode: string;
  setSelectedCurrency: (currency: string) => void;
  setMonetaryAmount: (amount: number) => void;
  widgetConfiguration: IWidgetConfiguration
  setActiveStep: (step: number) => void;
  isZeroDecimalCurrency: boolean;
  quotingErrorMessage: string | null;
  co2amountKg: number;
  setCo2amountKg: (amount: number) => void;
}

const EstimationPage: FC<IEstimationPage> = ({
  monetaryAmount,
  quote,
  selectedCurrency,
  isLoading,
  basket,
  onOffsetClick,
  merchantCode,
  setSelectedCurrency,
  setMonetaryAmount,
  widgetConfiguration ,
  setActiveStep,
  isZeroDecimalCurrency,
  quotingErrorMessage,
  co2amountKg,
  setCo2amountKg
}) => {

  const [co2massUnit,setCo2massUnit] = useState<MassUnit>("kg");
  const [co2amount,setCo2amount] = useState<string>(monetaryAmount > 0 ? "" : convert(co2amountKg,"kg",co2massUnit).toFixed(0));
  const [monetaryAmountUnprocessed,setMonetaryAmountUnprocessed] = useState<string>(monetaryAmount.toFixed(isZeroDecimalCurrency ? 0 : 2));
  const [validationErrorMessage,setValidationErrorMessage] = useState<string>('');

  const onMassFieldUpdate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    let value = (e.target.value || '')
        .replace(/[^0-9]/g,'') // remove non-digit symbols
    setMonetaryAmountUnprocessed("");
    setCo2amount(value);
  }, [])

  const onCostFieldUpdate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    let value = (e.target.value || '')
        .replace(/[^0-9.]/g,'') // remove non-digit symbols
        .replace(/(\.[.]+)/g, '.') // remove excess decimal dotts
        .replace((isZeroDecimalCurrency ? /(\d*).*$/ : /(\d*\.\d{1,2}).*$/),'$1') // remove excess decimal places
    setCo2amount("");
    setMonetaryAmountUnprocessed(value);
  }, [isZeroDecimalCurrency])

  useEffect(() => {
    const toNumber = Number(monetaryAmountUnprocessed);
    if (Number.isNaN(toNumber) || toNumber <= 0) {
      setMonetaryAmount(0);
    } else {
      setMonetaryAmount(toNumber);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [monetaryAmountUnprocessed])

  useEffect(() => {
    const toNumber = Number(co2amount);
    if (Number.isNaN(toNumber) || toNumber <= 0) {
      setCo2amountKg(0);
    } else {
      setCo2amountKg(convert(toNumber, co2massUnit, "kg"));
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [co2amount,co2massUnit])

  useEffect(() => {
    if (Number(co2amount) || Number(monetaryAmountUnprocessed)) {
      setValidationErrorMessage("");
    } else {
      setValidationErrorMessage("Please, specify valid amount");
    } 
  }, [co2amount,monetaryAmountUnprocessed]);

  useEffect(() => {
    if (Number(monetaryAmount) !== Number(monetaryAmountUnprocessed)) { 
      setMonetaryAmountUnprocessed(monetaryAmount.toFixed(isZeroDecimalCurrency ? 0 : 2));
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [monetaryAmount]);

  useEffect(() => {
    if (Number.isNaN(Number(co2amount)) || convert(Number(co2amount), co2massUnit, "kg") !== Number(co2amountKg)) {
      setCo2amount(convert(Number(co2amountKg), "kg", co2massUnit).toFixed(0));
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [co2amountKg]);


  const renderCurrencyList = useMemo(() =>
    SUPPORTED_CURRENCIES.map((currency) => ({ value: currency, name: currency }))
  , []);
  
  const renderMassUnitList = useMemo(() =>
    MASS_UNITS.map((u: MassUnit) => ({ value: u, name: u }))
  , []);

  //Please don't turn this into html (eg adding bold tags) as it will break the translation
  function getSummaryText() {
    if((monetaryAmount || co2amountKg) && quote && !validationErrorMessage && !quotingErrorMessage && !isLoading){
      const quotedUnit = quote.carbonOffsetQuantity?.unit.toLowerCase() as MassUnit;
      const quotedValue = quote.carbonOffsetQuantity?.value;
      const convertedValue = convert(quotedValue, quotedUnit, co2massUnit);
      const formattedConverted = convertedValue >= 1
        ? formatNoDecimals(Math.round(convertedValue))
        : convertedValue.toLocaleString(undefined, { maximumSignificantDigits: 2 });
      const conversionText = quotedUnit !== co2massUnit ? `(${formattedConverted}${co2massUnit}) ` : "";
      return `Your ${quote.offsetPrice?.currency && getCurrencySymbol(quote.offsetPrice.currency)}${formatMoney(quote.offsetPrice?.value, isZeroDecimalCurrency)} ${quote.offsetPrice?.currency?.toUpperCase()} contribution offsets ${quotedValue && formatNoDecimals(Math.round(quotedValue))} ${quotedUnit} ${conversionText}of CO₂ emissions.`
    }
    else{
      return ""
    }
  }

  return (
      <div className={`${styles.container} delayed-fadein`}>
        <div>
          <PageHeading
            title={'Your contribution'}
            activeStep={0}
            setActiveStep={setActiveStep}
          />

          <div className={styles.marginTop}>
            <p className={styles.textBold}>{"Please confirm your contribution."}</p>
          </div>

          <div className={`${styles.amountInputGroup}`}>
            {widgetConfiguration.enableCo2amountField && <>
            <div className={`${styles.selectWrapper} ${validationErrorMessage ? styles.selectWrapperError : ''}`}>
              <input
                // do NOT add `type={"number"}` - browser doesnt send events for invalid amount
                inputMode={"numeric"}
                step={"1"}
                className={styles.amountInput}
                value={co2amount}
                onChange={onMassFieldUpdate}
                placeholder={"Amount"}
              />
              <div className={`${styles.selectDropdownWrapper}`}><Select
                disablePadding={true}
                onChange={(value) => {
                    setCo2massUnit(value);
                }}
                value={co2massUnit}
                optionsList={renderMassUnitList}
                disabled={false}
                className={'no-translate'}
              /></div>
            </div>
            <div>or</div></>}
            <div className={`${styles.selectWrapper} ${validationErrorMessage ? styles.selectWrapperError : ''}`}>
              <input
                // do NOT add `type={"number"}` - browser doesnt send events for invalid amount
                inputMode={"numeric"}
                step={isZeroDecimalCurrency ? "1" : "0.01"}
                className={styles.amountInput}
                value={monetaryAmountUnprocessed}
                onChange={onCostFieldUpdate}
                placeholder={"Cost"}
              />
              <div className={`${styles.selectDropdownWrapper}`}><Select
                disablePadding={true}
                onChange={(value) => {
                    setSelectedCurrency(value);
                }}
                value={selectedCurrency}
                optionsList={renderCurrencyList}
                disabled={false}
                className={'no-translate'}
              /></div>
            </div>
          </div>
          <div className={`${styles.text} ${styles.errorMessage}`} style={{marginBottom: '-1rem'}}>
            {validationErrorMessage || <>&nbsp;</>}
          </div>
          <div className={styles.marginTop}>
            <p className={styles.text}>{"You are supporting real projects that make a meaningful difference for the planet."}</p>
            <BasketInformation merchantCode={merchantCode} projects={basket?.projects} hideLink={true}/>
          </div>
      </div>
      <div className={styles.controls}>
      <p className={`${styles.text} ${quotingErrorMessage ? styles.errorMessage : ''} ${!isLoading ? 'delayed-fadein' : ''}`}
         style={{minHeight: '3rem'}}> 
        {/*isLoading condition ensures that a new quote loading removes then re-adds the fadein class for non-english*/}
        {quotingErrorMessage || getSummaryText()}
      </p>
        <div className={styles.marginTop}>
          <Button
            variant={"primary"}
            isLoading={isLoading}
            text={"Continue to payment"}
            onClick={() => onOffsetClick()}
            disabled={isLoading || !(co2amountKg || monetaryAmount) || !(co2amount || monetaryAmountUnprocessed) || !quote || !!validationErrorMessage || !!quotingErrorMessage}
          />
        </div>
      </div>

      {!widgetConfiguration.hidePoweredBy && <Footer/>}
    </div>
  );
};

export default EstimationPage;
